import React from 'react';
import { FormControlProps, Button, IconButton, FormControl, InputProps } from '@cad-arc';
import { useIntegratedForm } from '../use-integrated-form';
import { useInputSizeForApplicationType } from '../../common/hooks/use-input-size-for-application-type';

export type FormButtonProps = Partial<React.ComponentProps<typeof Button>> & {
    length?: FormControlProps['length'];
    showInline?: boolean;
    asIcon?: boolean;
};

export function FormButtonBase(props: FormButtonProps): React.ReactElement {
    const {size: propsSize, ...otherProps} = props;

    const size = useInputSizeForApplicationType(propsSize as InputProps['size'], { frSizeOverride: 'lg' });

    if (props.showInline) {
        return (
            <FormControl
                label=""
                size={size as FormControlProps['size']}
                length={props.length}
            >
                {props.asIcon ? (
                    <IconButton size={size} {...otherProps} />
                ) : (
                    <Button size={size} {...otherProps} />
                )}
            </FormControl>
        );
    }

    if (props.asIcon) {
        return <IconButton size={size} {...otherProps} />;
    }

    return <Button size={size} {...otherProps} />;
}

export function FormButton(props: FormButtonProps): React.ReactElement {
    const integratedForm = useIntegratedForm();
    const buttonPropsMemo: FormButtonProps = React.useMemo(() => {
        return {
            ...integratedForm.getGenericButtonProps(),
            ...props,
        };
    }, [props, integratedForm]);

    return <FormButtonBase {...buttonPropsMemo} />;
}

/**
 * IconButtons used to interact with form state
 */
export function FormInputIconButton(props: FormButtonProps): React.ReactElement {
    const integratedForm = useIntegratedForm();
    const buttonPropsMemo: FormButtonProps = React.useMemo(() => {
        return {
            ...integratedForm.getFormInputButtonProps(),
            ...props,
        };
    }, [props, integratedForm]);

    return <FormButtonBase asIcon {...buttonPropsMemo} />;
}

/**
 * Button used to interact with form state
 */
export function FormInputButton(props: FormButtonProps): React.ReactElement {
    const integratedForm = useIntegratedForm();
    const buttonPropsMemo: FormButtonProps = React.useMemo(() => {
        return {
            ...integratedForm.getFormInputButtonProps(),
            ...props,
        };
    }, [props, integratedForm]);

    return <FormButtonBase {...buttonPropsMemo} />;
}
